import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AuthLayout from "../components/auth/AuthLayout";
import TurnstileWidget, { type TurnstileWidgetRef } from "../components/auth/TurnstileWidget";
import { verifyInvitationToken, acceptInvitation, type VerifyInvitationResponse } from "../api/accessRequestsApi";

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [verifying, setVerifying] = useState(true);
  const [invitationData, setInvitationData] = useState<VerifyInvitationResponse | null>(null);
  const [verifyError, setVerifyError] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [activated, setActivated] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  useEffect(() => {
    if (!token.trim()) {
      setVerifying(false);
      setVerifyError("No invitation token provided. Please check the link in your invitation email.");
      return;
    }

    let isMounted = true;
    verifyInvitationToken(token.trim())
      .then((data) => {
        if (isMounted) {
          setInvitationData(data);
          setVerifying(false);
        }
      })
      .catch((err: unknown) => {
        if (isMounted) {
          const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
          setVerifyError(
            axiosErr.response?.data?.detail ||
            axiosErr.message ||
            "This invitation link is invalid, expired, or has already been used."
          );
          setVerifying(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await acceptInvitation({
        token: token.trim(),
        password,
        confirm_password: confirmPassword,
        turnstile_token: turnstileToken || undefined,
      });

      setActivated(true);
      setSuccessMessage(res.message);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      setSubmitError(
        axiosErr.response?.data?.detail ||
        axiosErr.message ||
        "Failed to initialize account credentials. Please retry."
      );
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      {verifying ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <svg
            className="animate-spin"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#10b981"
            strokeWidth="2.5"
            style={{ margin: "0 auto 16px auto", display: "block" }}
          >
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" />
          </svg>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>
            Verifying cryptographic invitation token...
          </p>
        </div>
      ) : verifyError ? (
        <div style={{ textAlign: "center", padding: "16px 0 24px 0" }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px auto",
              color: "#ef4444",
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>

          <h2 className="tx-auth-form-title" style={{ marginBottom: 10 }}>
            Invitation Expired or Invalid
          </h2>
          <p className="tx-auth-form-subtitle" style={{ maxWidth: 420, margin: "0 auto 24px auto", lineHeight: "1.6" }}>
            {verifyError}
          </p>

          <Link
            to="/login"
            className="tx-auth-btn-primary"
            style={{ display: "inline-block", textDecoration: "none", width: "100%", textAlign: "center" }}
          >
            Return to Threatactix Home
          </Link>
        </div>
      ) : activated ? (
        <div style={{ textAlign: "center", padding: "16px 0 24px 0" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "rgba(16, 185, 129, 0.12)",
              border: "1px solid rgba(16, 185, 129, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px auto",
              color: "#10b981",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>

          <h2 className="tx-auth-form-title" style={{ marginBottom: 10 }}>
            Account Initialized
          </h2>
          <p className="tx-auth-form-subtitle" style={{ maxWidth: 420, margin: "0 auto 24px auto", lineHeight: "1.6" }}>
            {successMessage}
          </p>

          <div
            style={{
              background: "#141922",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
              textAlign: "left",
              fontSize: 13,
              color: "#94a3b8",
            }}
          >
            <div style={{ color: "#f8fafc", fontWeight: 600, marginBottom: 4 }}>
              Account Verification Complete
            </div>
            <p style={{ margin: 0, lineHeight: "20px" }}>
              Your credentials are set and your email (<strong>{invitationData?.email}</strong>) has been verified. You will be able to log in normally as soon as the launch switch is turned on.
            </p>
          </div>

          <Link
            to="/login"
            className="tx-auth-btn-primary"
            style={{ display: "inline-block", textDecoration: "none", width: "100%", textAlign: "center" }}
          >
            Return to Sign-In
          </Link>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 12 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 9999,
                background: "rgba(16, 185, 129, 0.12)",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                color: "#10b981",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.8,
                textTransform: "uppercase",
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981" }} />
              Verified Invitation
            </span>
          </div>

          <h2 className="tx-auth-form-title" style={{ marginBottom: 6 }}>
            Set Your Password
          </h2>
          <p className="tx-auth-form-subtitle" style={{ marginBottom: 20 }}>
            Welcome, <strong>{invitationData?.full_name}</strong>. Configure your credentials to activate your Threatactix account.
          </p>

          <div
            style={{
              background: "#141922",
              border: "1px solid rgba(255, 255, 255, 0.06)",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 20,
              fontSize: 13,
              color: "#94a3b8",
            }}
          >
            <div><strong>Email:</strong> {invitationData?.email}</div>
            {invitationData?.company && <div><strong>Organization:</strong> {invitationData.company}</div>}
          </div>

          {submitError && (
            <div className="tx-auth-alert tx-auth-alert-error" role="alert" style={{ marginBottom: 20 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{submitError}</span>
            </div>
          )}

          <form className="tx-auth-form" onSubmit={handleSubmit} noValidate>
            <div className="tx-auth-field">
              <label className="tx-auth-label" htmlFor="inv-password">
                Password <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div className="tx-auth-input-wrapper">
                <input
                  id="inv-password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="tx-auth-input"
                  disabled={submitting}
                  minLength={8}
                />
                <button
                  type="button"
                  className="tx-auth-eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="tx-auth-field">
              <label className="tx-auth-label" htmlFor="inv-confirm-password">
                Confirm Password <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div className="tx-auth-input-wrapper">
                <input
                  id="inv-confirm-password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="tx-auth-input"
                  disabled={submitting}
                  minLength={8}
                />
              </div>
            </div>

            {/* Turnstile Widget */}
            <TurnstileWidget
              ref={turnstileRef}
              onSuccess={(tok) => setTurnstileToken(tok)}
              onError={() => setTurnstileToken("")}
              onExpire={() => setTurnstileToken("")}
            />

            <button
              type="submit"
              className="tx-auth-btn-primary"
              disabled={submitting}
              id="btn-accept-invitation"
              style={{ marginTop: 8 }}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  <span>Activating Account...</span>
                </>
              ) : (
                "Activate Threatactix Account"
              )}
            </button>
          </form>
        </div>
      )}
    </AuthLayout>
  );
}
